import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { CARD_TEXT_MAX, PACK_CARDS_MAX, cleanText } from '@shared/rules';
import type { CardInput, PackDetail, PackSummary } from '@shared/types';
import { AnswerCard, PromptCard } from '../components/Cards';
import { Footer, TopBar, copyText, useDocumentTitle, whatsappLink } from '../components/Chrome';
import { usePrefs } from '../components/Prefs';
import { api } from '../lib/api';
import { forgetMyPack, getMyPacks, getName, saveMyPack } from '../lib/storage';
import { accountApi } from './account/api';
import { PHOTO_CARDS_MAX, PhotoCardsEditor, draftsFromCards, draftsToCards, photoDraftProblem, type PhotoDraft } from './account/PhotoCards';
import { useAccount } from './account/session';

// ------------------------------------------------------------------ list

/** Packs on this device plus, when signed in, the ones on the account (which need no edit key). */
function useYourPacks(): { code: string; name: string; editKey: string }[] {
  const { user } = useAccount();
  const [accountPacks, setAccountPacks] = useState<PackSummary[]>([]);
  useEffect(() => {
    if (!user) return setAccountPacks([]);
    accountApi.packs().then(setAccountPacks).catch(() => setAccountPacks([]));
  }, [user]);
  const device = getMyPacks();
  const onDevice = new Set(device.map((p) => p.code));
  return [...device, ...accountPacks.filter((p) => !onDevice.has(p.code)).map((p) => ({ code: p.code, name: p.name, editKey: '' }))];
}

export function PacksPage() {
  useDocumentTitle('Card packs');
  const { status, user: signedIn } = useAccount();
  const user = status === 'unknown' ? undefined : signedIn;
  const [packs, setPacks] = useState<PackSummary[] | null>(null);
  const [error, setError] = useState('');
  const mine = useYourPacks();

  useEffect(() => {
    api.packs().then(setPacks).catch((e) => setError(e.message));
  }, []);

  return (
    <>
      <TopBar>
        <Link className="btn btn-sm btn-primary" to="/packs/new">
          Make a pack
        </Link>
      </TopBar>
      <main className="page stack">
        <h1>Card packs</h1>
        <p className="muted">The host picks packs in the lobby. Mix and match, or add your own family's pack by its code.</p>
        {error && <p className="error">{error}</p>}
        {!packs && !error && <p className="muted">Loading...</p>}
        <div className="stack">
          {packs?.map((p) => (
            <Link key={p.id} to={`/packs/${p.code}`} className="link-tile">
              <span className="row">
                <strong>{p.name}</strong>
                {p.nsfw && <span className="chip red">18+</span>}
                {p.isDefault && <span className="chip blue">on by default</span>}
                {p.kind === 'community' && <span className="chip orange">community</span>}
              </span>
              <span>{p.description}</span>
              <span>
                {p.promptCount} prompts · {p.answerCount} answers
              </span>
            </Link>
          ))}
        </div>

        <section className="panel stack" style={{ marginTop: 12 }}>
          <h2>Your packs</h2>
          {mine.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              Packs you make on this device show up here. Fill one with cousins' names, family sayings and inside jokes, then add it to any room with its code.
            </p>
          ) : (
            <ul className="player-list">
              {mine.map((m) => (
                <li key={m.code}>
                  <span className="name">
                    {m.name} <span className="chip orange">{m.code}</span>
                  </span>
                  <Link className="btn btn-sm btn-outline" to={m.editKey ? `/packs/${m.code}/edit#k=${m.editKey}` : `/packs/${m.code}/edit`}>
                    Edit
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {!user && user !== undefined && (
            <p className="muted small" style={{ margin: 0 }}>
              <Link to="/account?next=/packs">Sign in</Link> (optional) to keep your packs on every phone.
            </p>
          )}
          <Link className="btn btn-primary" to="/packs/new">
            Make a new pack
          </Link>
        </section>
      </main>
      <Footer />
    </>
  );
}

// ------------------------------------------------------------------ view

export function PackViewPage() {
  const code = (useParams().code ?? '').toUpperCase();
  const [pack, setPack] = useState<PackDetail | null>(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'prompt' | 'answer'>('prompt');
  const { toast } = usePrefs();
  useDocumentTitle(pack?.name ?? 'Pack');
  const mine = getMyPacks().find((m) => m.code === code);
  const { user } = useAccount();
  const [owned, setOwned] = useState(false);

  useEffect(() => {
    api.pack(code, mine?.editKey).then(setPack).catch((e) => setError(e.message));
  }, [code, mine?.editKey, user?.id]);

  useEffect(() => {
    if (!user || mine) return setOwned(false);
    api.canEditPack(code, '').then((r) => setOwned(r.ok)).catch(() => setOwned(false));
  }, [code, user, Boolean(mine)]);

  if (error) {
    return (
      <>
        <TopBar />
        <main className="page">
          <h1>Pack not found</h1>
          <p className="muted">{error}</p>
          <Link to="/packs">See all packs</Link>
        </main>
      </>
    );
  }
  if (!pack) return <TopBar />;

  const cards = pack.cards.filter((c) => c.type === filter);
  return (
    <>
      <TopBar>
        {(mine || owned) && (
          <Link className="btn btn-sm btn-outline" to={mine ? `/packs/${code}/edit#k=${mine.editKey}` : `/packs/${code}/edit`}>
            Edit
          </Link>
        )}
      </TopBar>
      <main className="page wide stack">
        <div>
          <div className="row">
            <h1 style={{ margin: 0 }}>{pack.name}</h1>
            {pack.nsfw && <span className="chip red">18+</span>}
          </div>
          {pack.description && <p className="muted" style={{ marginTop: 6 }}>{pack.description}</p>}
          <p className="small muted">
            {pack.kind === 'community' && pack.authorName ? `Made by ${pack.authorName} · ` : ''}
            {pack.promptCount} prompts · {pack.answerCount} answers
          </p>
        </div>
        {pack.kind === 'community' && (
          <div className="panel stack">
            <strong>To play this pack</strong>
            <span className="muted small">
              Start a game, then in the lobby type this code under "Card packs": <strong className="chip orange">{pack.code}</strong>
            </span>
            <div className="row">
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={async () => {
                  if (await copyText(pack.code)) toast('Pack code copied.');
                }}
              >
                Copy code
              </button>
              <a
                className="btn btn-sm btn-outline"
                href={whatsappLink(`Add our CompleteMasti pack "${pack.name}" to your game with code ${pack.code}: ${window.location.origin}/packs/${pack.code}`)}
                target="_blank"
                rel="noreferrer"
              >
                Share on WhatsApp
              </a>
            </div>
          </div>
        )}
        <div className="segmented" style={{ maxWidth: 360 }}>
          <button type="button" aria-pressed={filter === 'prompt'} onClick={() => setFilter('prompt')}>
            {pack.preview ? 'Sample prompts' : `Prompts (${pack.promptCount})`}
          </button>
          <button type="button" aria-pressed={filter === 'answer'} onClick={() => setFilter('answer')}>
            {pack.preview ? 'Sample answers' : `Answers (${pack.answerCount})`}
          </button>
        </div>
        {pack.preview && (
          <p className="muted small" style={{ margin: 0 }}>
            {pack.kind === 'official'
              ? `A few cards from the pack. The rest turn up when you play, or in the printed deck.`
              : `A few cards from the pack. The rest turn up when you play it.`}
          </p>
        )}
        <div className="card-list">
          {cards.map((c) => (c.type === 'prompt' ? <PromptCard key={c.id} card={c} /> : <AnswerCard key={c.id} card={c} />))}
        </div>
        {pack.preview && (
          <div className="row">
            <Link className="btn btn-primary" to="/">
              Play with this pack
            </Link>
            {pack.kind === 'official' && (
              <Link className="btn btn-outline" to="/shop">
                Get the printed deck
              </Link>
            )}
          </div>
        )}
      </main>
    </>
  );
}

// ------------------------------------------------------------------ editor

/** "text | translation | context" per line; the extra parts are optional. */
function parseLines(text: string, type: 'prompt' | 'answer', existing: Map<string, string>): CardInput[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [body, gloss = '', explanation = ''] = line.split('|').map((x) => cleanText(x));
      return { id: existing.get(`${type}|${body.toLowerCase()}`), type, text: body, gloss, explanation };
    })
    .filter((c) => c.text);
}

function toLines(cards: CardInput[], type: 'prompt' | 'answer'): string {
  return cards
    .filter((c) => c.type === type)
    .map((c) => [c.text, c.gloss, c.explanation].filter((x, i) => i === 0 || x || (i === 1 && c.explanation)).join(' | '))
    .join('\n');
}

export function PackEditorPage() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = usePrefs();
  const { status: accountStatus, user } = useAccount();
  const code = params.code?.toUpperCase();
  const editKey = new URLSearchParams(location.hash.slice(1)).get('k') ?? '';
  const editing = Boolean(code);
  useDocumentTitle(editing ? 'Edit pack' : 'Make a pack');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [authorName, setAuthorName] = useState(getName());
  const [nsfw, setNsfw] = useState(false);
  const [prompts, setPrompts] = useState('');
  const [answers, setAnswers] = useState('');
  const [photos, setPhotos] = useState<PhotoDraft[]>([]);
  const [existing, setExisting] = useState(new Map<string, string>());
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [loaded, setLoaded] = useState(!editing);
  const [created, setCreated] = useState<{ code: string; editKey: string } | null>(null);

  useEffect(() => {
    // Owners without the key are recognised by their session, so wait until we know who is signed in.
    if (!code || (!editKey && accountStatus === 'unknown')) return;
    (async () => {
      try {
        const ok = await api.canEditPack(code, editKey);
        if (!ok.ok) {
          setError(
            editKey || user
              ? 'This edit link is not valid. Only the person with the private edit link can change this pack.'
              : 'Only the pack maker can change this pack. If you made it while signed in, sign in first.',
          );
          return;
        }
        const pack = await api.pack(code, editKey);
        const textCards = pack.cards.filter((c) => !c.imageUrl);
        setName(pack.name);
        setDescription(pack.description);
        setAuthorName(pack.authorName);
        setNsfw(pack.nsfw);
        setPrompts(toLines(textCards, 'prompt'));
        setAnswers(toLines(textCards, 'answer'));
        setPhotos(draftsFromCards(pack.cards));
        setExisting(new Map(textCards.map((c) => [`${c.type}|${c.text.toLowerCase()}`, c.id])));
        if (editKey) saveMyPack({ code, editKey, name: pack.name });
        setError('');
        setLoaded(true);
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [code, editKey, accountStatus, user]);

  const parsed = useMemo(
    () => [...parseLines(prompts, 'prompt', existing), ...parseLines(answers, 'answer', existing)],
    [prompts, answers, existing],
  );
  const promptCount = parsed.filter((c) => c.type === 'prompt').length;
  const answerCount = parsed.length - promptCount;
  const badPrompt = parsed.find((c) => c.type === 'prompt' && !/_{3}/.test(c.text) && !/\?$/.test(c.text));
  const tooLong = parsed.find((c) => c.text.length > CARD_TEXT_MAX);

  /** Photo cards go first so they win if a text line happens to say the same thing. */
  const allCards = (drafts: PhotoDraft[]) => [...draftsToCards(drafts), ...parsed];

  /** Upload photos that are only on this phone so far; returns the drafts with their new image ids. */
  async function uploadPending(drafts: PhotoDraft[], auth?: { code: string; editKey: string }): Promise<PhotoDraft[]> {
    const pending = drafts.filter((d) => !d.imageId && d.blob);
    const done = new Map<string, string>();
    try {
      for (const [i, d] of pending.entries()) {
        setProgress(`Uploading photo ${i + 1} of ${pending.length}...`);
        const up = await accountApi.uploadImage(d.blob!, auth?.editKey ? auth : undefined);
        done.set(d.key, up.id);
      }
    } finally {
      setProgress('');
      // Remember what made it, so a retry doesn't upload the same photos again.
      if (done.size) setPhotos((ds) => ds.map((d) => (done.has(d.key) ? { ...d, imageId: done.get(d.key)!, blob: undefined } : d)));
    }
    return drafts.map((d) => (done.has(d.key) ? { ...d, imageId: done.get(d.key)!, blob: undefined } : d));
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!name.trim()) return setError('Give your pack a name.');
    if (!parsed.length && !photos.length) return setError('Add at least one card.');
    if (tooLong) return setError(`This card is too long (max ${CARD_TEXT_MAX} characters): "${tooLong.text.slice(0, 40)}..."`);
    const photoProblem = photoDraftProblem(photos, new Set(parsed.map((c) => `${c.type}|${c.text.toLowerCase()}`)));
    if (photoProblem) return setError(photoProblem);
    if (parsed.length + photos.length > PACK_CARDS_MAX) return setError(`Packs can hold up to ${PACK_CARDS_MAX} cards.`);
    setBusy(true);
    const meta = { name, description, authorName, nsfw };
    let fresh: { code: string; editKey: string } | null = null;
    try {
      if (editing && code) {
        const drafts = await uploadPending(photos, { code, editKey });
        await api.updatePack(code, editKey, { ...meta, cards: allCards(drafts) });
        if (editKey) saveMyPack({ code, editKey, name });
        toast('Pack saved.', 'success');
        navigate(`/packs/${code}`);
        return;
      }
      if (!user && photos.some((d) => !d.imageId)) {
        // Guests upload with the new pack's edit key, so the pack has to exist before its photos.
        fresh = await api.createPack({ ...meta, cards: allCards(photos.map((d) => ({ ...d, imageId: null }))) });
        saveMyPack({ code: fresh.code, editKey: fresh.editKey, name });
        const drafts = await uploadPending(photos, fresh);
        await api.updatePack(fresh.code, fresh.editKey, { ...meta, cards: allCards(drafts) });
      } else {
        const drafts = await uploadPending(photos);
        fresh = await api.createPack({ ...meta, cards: allCards(drafts) });
        saveMyPack({ code: fresh.code, editKey: fresh.editKey, name });
      }
      setCreated(fresh);
      window.scrollTo(0, 0);
    } catch (err) {
      if (fresh) {
        // The pack exists; only some photos are missing. Show the pack and say how to finish.
        setCreated(fresh);
        toast(`Your pack is saved, but some photos did not upload. ${(err as Error).message}`, 'error');
      } else {
        setError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!code || !window.confirm('Delete this pack for good? Rooms using it will stop drawing from it.')) return;
    try {
      await api.deletePack(code, editKey);
      forgetMyPack(code);
      toast('Pack deleted.');
      navigate('/packs');
    } catch (err) {
      setError((err as Error).message);
    }
  }

  if (created) {
    const editUrl = `${window.location.origin}/packs/${created.code}/edit#k=${created.editKey}`;
    return (
      <>
        <TopBar />
        <main className="page stack">
          <div className="center">
            <h1>Your pack is ready 🎉</h1>
            <p className="muted">Add it to any game from the lobby with this code:</p>
            <div className="big-code" style={{ color: 'var(--magenta)' }}>
              {created.code}
            </div>
          </div>
          <div className="panel stack">
            <strong>Private edit link</strong>
            <span className="muted small">
              {user
                ? 'Anyone with this link can change or delete the pack. It is also on your account, so you can edit it from any phone once you sign in.'
                : 'Anyone with this link can change or delete the pack. It is saved on this device; copy it somewhere safe if you want to edit from another phone.'}
            </span>
            <div className="copy-box">
              <span>{editUrl}</span>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={async () => {
                  if (await copyText(editUrl)) toast('Edit link copied.');
                }}
              >
                Copy
              </button>
            </div>
          </div>
          <div className="row">
            <Link className="btn btn-primary" to="/">
              Start a game
            </Link>
            <Link className="btn btn-outline" to={`/packs/${created.code}`}>
              View pack
            </Link>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <TopBar />
      <main className="page">
        <h1>{editing ? 'Edit your pack' : 'Make your own pack'}</h1>
        <p className="muted">
          Put in the jokes only your people get: cousins' nicknames, Dadi's sayings, that one wedding. Mix it with the official packs in any game.
        </p>
        {editing && !loaded ? (
          error ? (
            <div className="stack">
              <p className="error">{error}</p>
              {!user && accountStatus === 'ready' && (
                <Link className="btn btn-primary" to={`/account?next=${encodeURIComponent(`/packs/${code}/edit`)}`}>
                  Sign in
                </Link>
              )}
            </div>
          ) : (
            <p className="muted">Loading...</p>
          )
        ) : (
          <form className="stack" onSubmit={save}>
            <div className="panel stack">
              <label className="field">
                Pack name
                <input className="input" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} placeholder="The Shah Family Pack" required />
              </label>
              <label className="field">
                Description <span className="hint">Optional</span>
                <input className="input" maxLength={240} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Inside jokes from 20 years of Diwali parties" />
              </label>
              <label className="field">
                Made by <span className="hint">Optional</span>
                <input className="input" maxLength={40} value={authorName} onChange={(e) => setAuthorName(e.target.value)} />
              </label>
              <label className="check">
                <input type="checkbox" checked={nsfw} onChange={(e) => setNsfw(e.target.checked)} />
                <span>
                  18+ pack <span className="muted small">(hosts get a warning before adding it)</span>
                </span>
              </label>
            </div>

            <div className="panel stack">
              <label className="field">
                <span>Prompt cards <span className="muted">({promptCount})</span></span>
                <span className="hint">
                  One per line. Use ___ for each blank. Two blanks means players pick two cards. Add a translation or context after a | if you like:
                  <br />
                  <code>Dadi's cure for everything: ___.</code>
                  <br />
                  <code>Chalo, ___ ho gaya. | Okay, ___ is done. | What Mama says after every function</code>
                </span>
                <textarea className="input" style={{ minHeight: 180 }} value={prompts} onChange={(e) => setPrompts(e.target.value)} />
              </label>
              {badPrompt && <p className="muted small">Tip: "{badPrompt.text}" has no blank. That works for questions, otherwise add ___.</p>}
            </div>

            <div className="panel stack">
              <label className="field">
                <span>Answer cards <span className="muted">({answerCount})</span></span>
                <span className="hint">
                  One per line. Short, specific noun phrases are funniest:
                  <br />
                  <code>Pinky Masi's 45-minute voice notes</code>
                  <br />
                  <code>Jugaad | a creative hack, desi style</code>
                </span>
                <textarea className="input" style={{ minHeight: 240 }} value={answers} onChange={(e) => setAnswers(e.target.value)} />
              </label>
              <p className="muted small" style={{ margin: 0 }}>
                A pack can be played on its own once it has at least 5 prompts and about 40 answers. Smaller packs are best mixed with Classic.
              </p>
            </div>

            <PhotoCardsEditor drafts={photos} onChange={setPhotos} disabled={busy} />
            {photos.length >= PHOTO_CARDS_MAX && <p className="muted small">That's the most photo cards a pack can hold.</p>}

            {error && (
              <p className="error" role="alert">
                {error}
              </p>
            )}
            {progress && <p className="muted small">{progress}</p>}
            <div className="row">
              <button className="btn btn-primary btn-lg grow" disabled={busy}>
                {busy ? 'Saving...' : editing ? 'Save changes' : 'Create pack'}
              </button>
              {editing && (
                <button type="button" className="btn btn-danger" onClick={remove}>
                  Delete pack
                </button>
              )}
            </div>
          </form>
        )}
      </main>
    </>
  );
}
